import { SELF_ALLOCATION_RETIRED } from "@/features/finances/selfAllocationFeature";
import { useAuthStore } from "@/store/auth";
import { useNavBadgeCounts } from "@/hooks/useNavBadges";

/* Sekcja „Postęp prac" — widoki, które wcześniej stały osobno w menu („Postępy
   prac", „Rozkład pracy nad przedsięwzięciami", „Rozkład pracy — wnioski").
   Teraz żyją wewnątrz Friday (/tasks/board, /tasks/list), a ich badge są w
   sidebarze zsumowane do jednego licznika przy Friday. Ten hook jest wspólnym
   źródłem prawdy dla obu stron: sekcji w Friday (badge rozdzielone) i sidebara
   (badge zsumowany), żeby widoczność pozycji i liczniki nie rozjechały się.

   Rozkład pracy (oba wpisy samo-alokacji) został WYCOFANY — zob.
   features/finances/selfAllocationFeature.ts. Wpisy i ich liczniki znikają dla
   wszystkich; archiwum dla CEO stoi osobno w sidebarze. */

export type WorkProgressTone = "brand" | "red";

export interface WorkProgressLink {
  to: string;
  label: string;
  /* Podpowiedź pod kursorem — tłumaczy też znaczenie licznika. */
  title?: string;
  count: number;
  tone: WorkProgressTone;
  /* Źródło licznika. Ten sam licznik zasila dwa wpisy (widok właściciela i
     recenzenta wniosków samo-alokacji), więc suma zbiorcza liczy go raz. */
  badgeKey?: string;
}

export function useWorkProgressLinks(): WorkProgressLink[] {
  const user = useAuthStore((s) => s.user);
  // Cache-only reader: useNavBadges (leader election, polling) is mounted once, in
  // the Sidebar — this hook is called from the Sidebar too, and from Friday pages.
  const badges = useNavBadgeCounts();
  const selfAllocCount = badges.self_allocation_action;

  const links: WorkProgressLink[] = [];
  if (user?.role === "director") {
    links.push({
      to: "/tasks/progress",
      label: "Postępy prac",
      title: "Postępy prac pracowników",
      count: 0,
      tone: "brand",
    });
  }
  // Własny formularz rozkładu pracy — pracownik z can_self_allocate.
  if (
    !SELF_ALLOCATION_RETIRED &&
    user &&
    user.role !== "contractor" &&
    (user as any).can_self_allocate
  ) {
    links.push({
      to: "/finances/self-allocation",
      label: "Rozkład pracy nad przedsięwzięciami",
      title: "Rozkład pracy — wymaga Twojego działania",
      count: selfAllocCount,
      tone: "red",
      badgeKey: "self_allocation_action",
    });
  }
  // Panel wniosków — CEO albo dyrektor techniczny oddziału.
  if (
    !SELF_ALLOCATION_RETIRED &&
    user &&
    (user.is_ceo ||
      (user.role === "director" &&
        user.is_technical_director &&
        !!user.branch?.id))
  ) {
    links.push({
      to: "/finances/self-allocations-admin",
      label: "Rozkład pracy — wnioski",
      title: "Wnioski o rozkład pracy czekają na decyzję",
      count: selfAllocCount,
      tone: "red",
      badgeKey: "self_allocation_action",
    });
  }
  return links;
}

/* Suma dla zbiorczego badge'a przy Friday — każdy licznik liczony raz, nawet
   jeśli zasila kilka odnośników. */
export function workProgressBadgeTotal(links: WorkProgressLink[]): number {
  const seen = new Set<string>();
  let total = 0;
  links.forEach((l, i) => {
    const key = l.badgeKey ?? `#${i}`;
    if (seen.has(key)) return;
    seen.add(key);
    total += l.count;
  });
  return total;
}

/* Najpilniejszy kolor wśród niezerowych liczników — steruje kolorem badge'a
   zbiorczego (czerwony wygrywa z firmowym). */
export function workProgressBadgeTone(
  links: WorkProgressLink[],
): WorkProgressTone {
  return links.some((l) => l.count > 0 && l.tone === "red") ? "red" : "brand";
}
